import { useMutation } from "@tanstack/react-query";

import { useEffectOnUpdate } from "@util/hooks";

import type { NetworkInfo } from "@alch/dx-entities";
import {
  buildComposerRequestBody,
  buildComposerRequestUrl,
} from "@features/composer/lib/request";
import type { ComposerFormState } from "./lib/form.js";
import type { ComposerMethodConfig } from "./lib/methods.js";

interface UseMakeComposerRequestProps {
  methodConfig?: ComposerMethodConfig;
  networkInfo?: NetworkInfo;
}

const useMakeComposerRequest = ({
  methodConfig,
  networkInfo,
}: UseMakeComposerRequestProps) => {
  const mutation = useMutation<
    { body: string; ok: boolean },
    Error,
    ComposerFormState
  >({
    mutationFn: async (state) => {
      if (!methodConfig) {
        throw Error("Incomplete request");
      }

      const requestUrl = buildComposerRequestUrl({
        methodConfig,
        networkInfo,
        apiKey: state.app,
        query: state.query,
      });

      const response = await fetch(requestUrl, {
        method: methodConfig.httpMethod,
        ...(methodConfig.requestBody
          ? {
              headers: { "content-type": "application/json" },
              body: buildComposerRequestBody({
                methodConfig,
                body: state.body,
              }),
            }
          : {}),
      });

      const body = await response.text();

      return { body, ok: response.ok };
    },
    retry: false,
  });

  useEffectOnUpdate(() => {
    mutation.reset();
  }, [methodConfig, networkInfo?.id]);

  return mutation;
};

export default useMakeComposerRequest;
